import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { TelemetryService } from '../observability/telemetry.service';

export interface AuthUser {
  sub: string;
  preferredUsername: string;
  name: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  roles: string[];
  hasAccess: boolean;
  /**
   * Encadre au moins une personne dans l'annuaire Entra (`directReports`). Ce n'est
   * PAS un rôle Keycloak : Entra n'expose pas cette information dans un token, l'API
   * la résout au login et la porte dans la session. Peut être absent si l'API déployée
   * est antérieure à la vue Manager → `undefined` traité comme non manager.
   */
  isManager?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly telemetry = inject(TelemetryService);

  private readonly _user = signal<AuthUser | null>(null);

  readonly user = this._user.asReadonly();

  /**
   * Droit d'ÉCRITURE (source unique de vérité côté front) : seuls les rôles
   * qualité et IT peuvent muter — `role_service_client` est en lecture seule.
   * Miroir des policies backend : tout endpoint d'écriture renvoie 403 sinon.
   */
  readonly canWrite = computed(() => {
    const roles = this._user()?.roles ?? [];
    return roles.includes('role_qualite') || roles.includes('role_it');
  });

  /**
   * Accès à la vue Manager (activité nominative par personne). Miroir de la policy
   * backend : l'endpoint renvoie 403 sans ce statut.
   */
  readonly isManager = computed(() => this._user()?.isManager === true);

  /**
   * Vérifie l'état de la session côté API. Appelé par le authGuard à chaque
   * navigation vers une route protégée.
   */
  refreshSession(): Observable<AuthUser | null> {
    return this.http.get<AuthUser>('/api/auth/me').pipe(
      tap((user) => {
        this._user.set(user);
        // id + rôles uniquement (jamais email/nom) : pas de PII dans la télémétrie.
        this.telemetry.setUser({ id: user.sub, roles: user.roles });
      }),
      catchError(() => {
        this._user.set(null);
        this.telemetry.setUser(null);
        return of(null);
      }),
    );
  }

  /**
   * Redirige vers Keycloak via l'API. Navigation directe (pas un fetch)
   * parce qu'il y a une redirection HTTP 302 que fetch ne suit pas.
   */
  login(returnUrl: string = window.location.pathname + window.location.search): void {
    window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /**
   * Logout : POST vers /api/auth/logout. L'API retourne une redirection vers
   * Keycloak qui invalide la session puis revient sur le front.
   */
  logout(): void {
    // SignOut côté .NET retourne une redirection 302 (vers l'end-session Keycloak).
    // Un fetch ne suivrait pas cette redirection de navigation ; un form POST natif,
    // si — le navigateur enchaîne les 302 jusqu'au retour sur le front.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/auth/logout';
    document.body.appendChild(form);
    form.submit();
  }

  /** Met à jour l'état local sans round-trip API (utile après 401). */
  clear(): void {
    this._user.set(null);
    this.telemetry.setUser(null);
  }
}
